import { DependencyList, memo, useCallback, useEffect, useRef, useState } from 'react';
import logger from '@gral/log';

import _ from 'lodash';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { fetchData } from '@redux/slices/functionals/request/requestActions';
import { useSetState } from 'react-use';
import { IConfigComponent, IConfigStepper, ICompSetting, PaginatedResponseV3 } from '@ts/ui-models';
import { getConfiguration, IConfigOptions } from '../utils/AdvancedFetch';
import { postNewEntity } from '@services/ApiService';
import { CEntity, IEntity } from '@ts/ui-models';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';
import {
  EntitySelector,
  formatToEntities,
  IEntitySelectorWrapperProps,
  isCEntityType,
  TEntitySelectorEntity,
  TRows,
} from '@ts/storybook';

const log = logger(`WrapperEntitySelector`);
const WrapperEntitySelector = <Multiple extends boolean>(props: IEntitySelectorWrapperProps<Multiple>) => {
  const {
    value,
    label,
    seeAllSelected,
    allowCreateNewEntity,
    isReadOnly,
    disabled,
    required,
    valuesInOneLine,
    multiple,
    onChange,
    onChangeEntity,
    dataGridProps,
    ...restProps
  } = props;
  const textInputProps = { isReadOnly, disabled, required };
  const { configComponent } = props.searchComponent;
  const { componentId, mainClass } = configComponent;
  const dispatch = useAppDispatch();
  const globalSettings = useAppSelector(state => state.global.settings);
  const listACL = useAppSelector(state => state.global.aclList);
  if (!globalSettings) return;

  //type EntitySelectorValue<Multiple extends boolean> = Multiple extends true ? CEntity<IEntity>[] : CEntity<IEntity> | null;

  const [entitySelectorValue, setEntitySelectorValue] = useState<TEntitySelectorEntity<Multiple>>(
    (multiple ? [] : null) as TEntitySelectorEntity<Multiple>
  );

  const [fetchDone, setFetchDone] = useState(false);

  const getID = configComponent.fetchURL?.startsWith('/api/v3') ? 'id' : 'logicalId';

  const [state, setState] = useState({
    isOpenDropdown: false,
  });
  const canMoreFetchRef = useRef(true);
  const [options, setOptions] = useState<CEntity<IEntity>[]>([]);
  const [pagination, setPagination] = useSetState({
    page: 0,
    offset: 0,
    pageSize: 20,
    pageDownloaded: -1,
  });
  const [quickFilterValues, setQuickFilterValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(0);

  //#region Configuration & Settings
  const savedDataGridConfig = globalSettings.dataGridConfig?.[componentId];
  const mergedDataGridConfig = savedDataGridConfig;
  const mergedConfigComponent = { ...configComponent, componentId, ACL: listACL[configComponent.aclId] };

  const componentSettings = globalSettings.componentSettings[componentId];
  const componentSettingsDefault = configComponent.defaultId ? globalSettings.componentSettings[configComponent.defaultId] : {};
  const mergedComponentSettings: ICompSetting = {
    ...componentSettingsDefault,
    ...componentSettings,
  };
  //#endregion

  //#region Handlers
  const onChangeEndScroll = useCallback(() => {
    log.info('onChangeEndScroll');
    if (canMoreFetchRef.current) {
      setPagination(prevPagination => {
        if (!isLoading) {
          return {
            page: prevPagination.page + 1,
            offset: (prevPagination.page + 1) * prevPagination.pageSize,
          };
        } else {
          return prevPagination;
        }
      });
    }
  }, [isLoading]);

  const onChangeSearchText = useCallback((v: string | number) => {
    log.info('onChangeSearchText', v);
    resetSearch();
    setQuickFilterValues([v.toString()]);
  }, []);
  //#endregion

  //#region Function
  const resetSearch = () => {
    log.info('resetSearch');
    setOptions([]);
    canMoreFetchRef.current = true;
    setPagination({ page: 0, offset: 0, pageDownloaded: -1 });
  };
  //#region Fetch data
  const getFetchData = useCallback(
    async (customConfigComponent?: IConfigComponent, options?: IConfigOptions) => {
      const dataBodyTableV4 = getConfiguration(
        customConfigComponent ?? configComponent,
        { componentSettings: mergedComponentSettings, dataGridConfig: mergedDataGridConfig },
        {
          paginationModel: { pageSize: pagination.pageSize, page: pagination.page },
          filterModel: {
            items: [],
            quickFilterValues,
          },
        },
        options
      );

      return (
        await dispatch(
          fetchData({ key: 'dataGrid', url: mergedConfigComponent.fetchURL ?? '', dataBody: dataBodyTableV4, ...options })
        )
      ).payload?.data;
    },
    [dispatch, mergedConfigComponent]
  );

  const setOptionsFromFetchData = (data?: TRows<any> | PaginatedResponseV3<any[]> | any[]) => {
    if (data) {
      let formatRows: CEntity<IEntity>[] = [];
      if (data && typeof data === 'object' && 'count' in data) {
        //V1 or V2
        formatRows = formatToEntities<true>(data.rows, mainClass);
      } else if (Array.isArray(data)) {
        //V1 or V2
        formatRows = formatToEntities<true>(data, mainClass);
      } else {
        //V3
        formatRows = formatToEntities<true>(data.data, mainClass);
      }
      setOptions(prev => [...prev, ...formatRows]);
      if (formatRows.length < pagination.pageSize) {
        canMoreFetchRef.current = false;
      }
      setFetchDone(true);
    } else {
      setIsLoading(0);
    }
  };

  const findEntitiesByValues = async (values?: string[] | null) => {
    if (values && (!entitySelectorValue || (Array.isArray(entitySelectorValue) && entitySelectorValue.length === 0))) {
      const customConfig = { ...configComponent, where: { [props.fieldToReturn ?? getID]: values } };

      const entities: any = await getFetchData(customConfig, {
        withoutPagination: true,
        verificationFetch: props.verificationFetch,
        withoutCancel: true,
      });
      const formatEntities: CEntity<IEntity>[] = formatToEntities<true>(
        (entities?.data as any[]) ?? (entities?.rows as any[]) ?? (entities as any[]),
        mainClass
      );

      if (!formatEntities || !Array.isArray(formatEntities) || formatEntities?.length === 0) return;

      if (!formatEntities || formatEntities?.length === 0) return;

      setEntitySelectorValue(formatEntities.length ? (!props.multiple ? formatEntities[0] : formatEntities) : (null as any));
      if (props.value !== entitySelectorValue)
        onChangeEntity?.(formatEntities.length ? (!props.multiple ? formatEntities[0] : formatEntities) : (null as any));
    }
  };

  const createNewEntity = async (inputValue: string) => {
    log.info('CreateNewEntity', inputValue);
    const newEntity = await postNewEntity(dispatch, configComponent as IConfigStepper, { name: inputValue });
    if (!newEntity) return;
    const formatNewEntity = formatToEntities<false>(newEntity, mainClass);

    if (formatNewEntity) {
      setOptions(prev => [...prev, formatNewEntity]);
      setState({ ...state, isOpenDropdown: props.multiple ? true : false });
    }
  };
  //#endregion
  //#endregion

  //#region Effect
  const effectFetchData = (deps: DependencyList) => {
    useEffect(() => {
      if (state.isOpenDropdown) {
        if (pagination.pageDownloaded < pagination.page) {
          setFetchDone(false);
          setIsLoading(1);
          if (canMoreFetchRef.current) {
            getFetchData().then(data => setOptionsFromFetchData(data));
          }
        }
      }
    }, deps);
  };

  const effectAfterFetchData = (deps: DependencyList) => {
    const isFirstRun = useRef(true);
    useEffect(() => {
      if (isFirstRun.current) {
        isFirstRun.current = false;
      } else {
        setIsLoading(0);
        setPagination({ pageDownloaded: pagination.page });
      }
    }, deps);
  };

  /**
   * Handles the preloading of data based on the entitySelectorValue, in case values do not arrive as expected.
   *
   * @param {DependencyList} deps - List of dependencies for the effect
   */
  const effectPreloadData = (deps: DependencyList) => {
    useEffect(() => {
      const { value } = props;
      if ((multiple && !value) || (Array.isArray(value) && value.length === 0) || (Array.isArray(value) && value[0] === null)) {
        setEntitySelectorValue(null as any);
        return;
      }

      if (isCEntityType<Multiple>(value)) {
        setEntitySelectorValue(value as any);
        return;
      }

      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          if (typeof value[0] === 'string') {
            findEntitiesByValues(value as string[]);
          } else {
            setEntitySelectorValue(formatToEntities(value, mainClass) as any);
          }
        } else {
          setEntitySelectorValue(formatToEntities(value, mainClass) as any);
        }
        return;
      }

      if (value && typeof value === 'string') findEntitiesByValues([value]);
    }, deps);
  };

  const effectReset = (deps: DependencyList) => {
    useEffectAfterMount(() => {
      log.info('Reset', configComponent);
      resetSearch();
    }, deps);
  };
  //#endregion

  effectFetchData([state.isOpenDropdown, pagination, quickFilterValues]);
  effectAfterFetchData([fetchDone]);
  effectPreloadData([value, multiple]);
  effectReset([configComponent.fetchURL]);

  return (
    <EntitySelector
      {...restProps}
      value={entitySelectorValue}
      onChange={v => onChange?.(v as any)}
      onChangeEntity={v => onChangeEntity?.(v as any)}
      options={options}
      multiple={multiple}
      stProps={{
        autocomplete: {
          label: label,
          loading: Boolean(isLoading),
          seeAllSelected: seeAllSelected,
          ...(valuesInOneLine ? { limitTags: 1 } : { limitTags: props.stProps?.autocomplete?.limitTags }),
          onChangeEndScroll: onChangeEndScroll,
          onChangeSearchText: onChangeSearchText,
          createNewEntity: allowCreateNewEntity ? createNewEntity : undefined,
          onOpen: () => setState({ ...state, isOpenDropdown: true }),
          onClose: () => setState({ ...state, isOpenDropdown: false }),
          stProps: { textInput: textInputProps },
        },
      }}
      dataGridProps={dataGridProps}
    />
  );
};

const MemoizedEntitySelectorWrapper = <Multiple extends boolean>(props: IEntitySelectorWrapperProps<Multiple>) => {
  return <WrapperEntitySelector {...props} />;
};

export default memo(MemoizedEntitySelectorWrapper) as typeof MemoizedEntitySelectorWrapper;
